// Localização: frontend/src/components/Diario/SinaisVitaisModal.jsx
// Modal específico para coletar sinais vitais ao concluir uma tarefa

import React, { useState } from 'react';
import Modal from '../UI/Modal';
import Alert from '../UI/Alert';
import { Heart, XCircle, Save } from 'lucide-react';

/**
 * Componente InputField auxiliar para campos de sinais vitais
 */
const InputField = ({ label, name, type, step, value, onChange, placeholder, min, max, error }) => (
    <div>
        <label htmlFor={name} className="block text-xs font-medium text-gray-600 mb-1">
            {label}
        </label>
        <input
            type={type}
            step={step}
            name={name}
            id={name}
            value={value || ''}
            onChange={onChange}
            placeholder={placeholder}
            min={min}
            max={max}
            className={`mt-1 block w-full border rounded-md shadow-sm p-2 text-sm ${
                error ? 'border-red-300 focus:ring-red-500 focus:border-red-500' : 'border-gray-300 focus:ring-emerald-500 focus:border-emerald-500'
            }`}
        />
        {error && <p className="mt-1 text-xs text-red-600">{error}</p>}
    </div>
);

/**
 * Modal para coletar sinais vitais ao concluir uma tarefa
 * @param {boolean} isOpen - Controla a visibilidade do modal
 * @param {function} onClose - Função para fechar o modal
 * @param {object} tarefa - Objeto da tarefa que está sendo concluída
 * @param {function} onConfirm - Função chamada ao confirmar (recebe dados dos sinais vitais)
 */
const SinaisVitaisModal = ({ isOpen, onClose, tarefa, onConfirm }) => {
    const [formData, setFormData] = useState({
        temperatura: '',
        pressao_sistolica: '',
        pressao_diastolica: '',
        freq_cardiaca: '',
        freq_respiratoria: '',
        saturacao_o2: '',
        observacoes: ''
    });

    const [errors, setErrors] = useState({});
    const [isSubmitting, setIsSubmitting] = useState(false);

    // Validações de ranges
    const validations = {
        temperatura: { min: 35.0, max: 42.0, message: 'Temperatura deve estar entre 35.0 e 42.0 °C' },
        pressao_sistolica: { min: 80, max: 200, message: 'Pressão sistólica deve estar entre 80 e 200 mmHg' },
        pressao_diastolica: { min: 40, max: 120, message: 'Pressão diastólica deve estar entre 40 e 120 mmHg' },
        freq_cardiaca: { min: 40, max: 200, message: 'Frequência cardíaca deve estar entre 40 e 200 bpm' },
        freq_respiratoria: { min: 10, max: 40, message: 'Frequência respiratória deve estar entre 10 e 40 irpm' },
        saturacao_o2: { min: 70, max: 100, message: 'Saturação O₂ deve estar entre 70 e 100%' }
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
        
        // Limpa erro do campo quando o usuário começa a digitar
        if (errors[name]) {
            setErrors(prev => {
                const newErrors = { ...prev };
                delete newErrors[name];
                return newErrors;
            });
        }
    };

    const validateField = (name, value) => {
        if (!value || value === '') return null; // Campos são opcionais
        
        const numValue = parseFloat(value);
        if (isNaN(numValue)) {
            return `${name} deve ser um número válido`;
        }

        const validation = validations[name];
        if (validation) {
            if (numValue < validation.min || numValue > validation.max) {
                return validation.message;
            }
        }

        return null;
    };

    const validateForm = () => {
        const newErrors = {};
        let hasErrors = false;

        // Valida cada campo que foi preenchido
        Object.keys(formData).forEach(key => {
            if (key !== 'observacoes' && formData[key]) {
                const error = validateField(key, formData[key]);
                if (error) {
                    newErrors[key] = error;
                    hasErrors = true;
                }
            }
        });

        setErrors(newErrors);
        return !hasErrors;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        
        if (!validateForm()) {
            return;
        }

        setIsSubmitting(true);

        try {
            // Prepara dados de sinais vitais (remove campos vazios)
            const sinaisVitais = {};
            Object.keys(formData).forEach(key => {
                if (key !== 'observacoes' && formData[key] && formData[key] !== '') {
                    const numValue = parseFloat(formData[key]);
                    if (!isNaN(numValue)) {
                        sinaisVitais[key] = numValue;
                    }
                }
            });

            // Verifica se pelo menos um sinal vital foi preenchido
            if (Object.keys(sinaisVitais).length === 0) {
                setErrors({ 
                    _general: 'Preencha pelo menos um sinal vital antes de concluir a tarefa.' 
                });
                setIsSubmitting(false);
                return;
            }

            // Chama a função de confirmação passada pelo componente pai
            await onConfirm({
                sinaisVitais,
                observacoes: formData.observacoes || null
            });

            // Limpa o formulário após sucesso
            setFormData({
                temperatura: '',
                pressao_sistolica: '',
                pressao_diastolica: '',
                freq_cardiaca: '',
                freq_respiratoria: '',
                saturacao_o2: '',
                observacoes: ''
            });
            setErrors({});
            
        } catch (error) {
            console.error('Erro ao registrar sinais vitais:', error);
            setErrors({ 
                _general: error.message || 'Erro ao registrar sinais vitais. Tente novamente.' 
            });
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleClose = () => {
        // Limpa erros e reseta formulário ao fechar
        setErrors({});
        setFormData({
            temperatura: '',
            pressao_sistolica: '',
            pressao_diastolica: '',
            freq_cardiaca: '',
            freq_respiratoria: '',
            saturacao_o2: '',
            observacoes: ''
        });
        onClose();
    };

    return (
        <Modal
            isOpen={isOpen}
            onClose={handleClose}
            title="📊 Registrar Sinais Vitais"
            size="md"
        >
            {errors._general && (
                <div className="mb-4">
                    <Alert type="error" message={errors._general} />
                </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-4">
                {/* Grid de Sinais Vitais */}
                <div className="grid grid-cols-2 gap-4 p-4 border rounded-lg bg-gray-50">
                    <div className="col-span-2 mb-2">
                        <h4 className="text-sm font-semibold text-gray-700 flex items-center gap-2">
                            <Heart size={16} className="text-red-500" />
                            Dados dos Sinais Vitais
                        </h4>
                        <p className="text-xs text-gray-500 mt-1">Preencha os campos disponíveis (todos são opcionais, mas pelo menos um é necessário)</p>
                    </div>

                    <InputField
                        label="Temperatura (°C)"
                        name="temperatura"
                        type="number"
                        step="0.1"
                        value={formData.temperatura}
                        onChange={handleChange}
                        placeholder="36.5"
                        min="35.0"
                        max="42.0"
                        error={errors.temperatura}
                    />

                    <InputField
                        label="Frequência Cardíaca (bpm)"
                        name="freq_cardiaca"
                        type="number"
                        value={formData.freq_cardiaca}
                        onChange={handleChange}
                        placeholder="75"
                        min="40"
                        max="200"
                        error={errors.freq_cardiaca}
                    />

                    <InputField
                        label="Pressão Sistólica (mmHg)"
                        name="pressao_sistolica"
                        type="number"
                        value={formData.pressao_sistolica}
                        onChange={handleChange}
                        placeholder="120"
                        min="80"
                        max="200"
                        error={errors.pressao_sistolica}
                    />

                    <InputField
                        label="Pressão Diastólica (mmHg)"
                        name="pressao_diastolica"
                        type="number"
                        value={formData.pressao_diastolica}
                        onChange={handleChange}
                        placeholder="80"
                        min="40"
                        max="120"
                        error={errors.pressao_diastolica}
                    />

                    <InputField
                        label="Frequência Respiratória (irpm)"
                        name="freq_respiratoria"
                        type="number"
                        value={formData.freq_respiratoria}
                        onChange={handleChange}
                        placeholder="16"
                        min="10"
                        max="40"
                        error={errors.freq_respiratoria}
                    />

                    <InputField
                        label="Saturação O₂ (%)"
                        name="saturacao_o2"
                        type="number"
                        value={formData.saturacao_o2}
                        onChange={handleChange}
                        placeholder="98"
                        min="70"
                        max="100"
                        error={errors.saturacao_o2}
                    />
                </div>

                {/* Campo de Observações */}
                <div>
                    <label htmlFor="observacoes" className="block text-sm font-medium text-gray-700 mb-1">
                        Observações (Opcional)
                    </label>
                    <textarea
                        name="observacoes"
                        id="observacoes"
                        value={formData.observacoes}
                        onChange={handleChange}
                        placeholder="Adicione observações sobre os sinais vitais ou o estado do paciente..."
                        rows="3"
                        className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm p-2 text-sm focus:ring-emerald-500 focus:border-emerald-500"
                    />
                </div>

                {/* Informação da Tarefa */}
                {tarefa && (
                    <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg">
                        <p className="text-xs text-blue-800">
                            <span className="font-semibold">Tarefa:</span> {tarefa.descricao || tarefa.tipo_tarefa}
                        </p>
                        <p className="text-xs text-blue-700 mt-1">
                            <span className="font-semibold">Horário Agendado:</span> {
                                tarefa.hora_agendada 
                                    ? new Date(tarefa.hora_agendada).toLocaleString('pt-BR')
                                    : 'N/A'
                            }
                        </p>
                    </div>
                )}

                {/* Botões */}
                <div className="flex justify-end gap-3 pt-4 border-t">
                    <button
                        type="button"
                        onClick={handleClose}
                        disabled={isSubmitting}
                        className="flex items-center gap-1 bg-gray-200 hover:bg-gray-300 text-gray-700 font-semibold py-2 px-4 rounded-lg transition disabled:opacity-50"
                    >
                        <XCircle size={18} /> Cancelar
                    </button>

                    <button
                        type="submit"
                        disabled={isSubmitting}
                        className="flex items-center gap-2 bg-emerald-600 hover:bg-emerald-700 text-white font-semibold py-2 px-4 rounded-lg shadow-md transition disabled:opacity-50"
                    >
                        {isSubmitting ? (
                            <>Salvando...</>
                        ) : (
                            <>
                                <Save size={18} /> Registrar e Concluir Tarefa
                            </>
                        )}
                    </button>
                </div>
            </form>
        </Modal>
    );
};

export default SinaisVitaisModal;

